import { useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import ArrowForward from '@mui/icons-material/ArrowForward';
import {
  Box,
  Button,
  FormLabel,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Controller, useForm, type SubmitHandler } from 'react-hook-form';
import { loginValidationSchema, type LoginValidationData } from './schema';
import googleLogo from '@/assets/images/logos/google.svg';
import microsoftLogo from '@/assets/images/logos/microsoft.svg';
import { LocalStorage } from '@/constants';
import { useNotification } from '@/context/notification';
import { cpfMask } from '@/utils/masks/cpf';
import { useCheckEmail } from '@/data/auth';
import { useNavigate } from '@/hooks/use-navigation';

interface EmailCpfProps {
  nextStep: (identifier: string) => void;
}

export const EmailCpf = ({ nextStep }: EmailCpfProps) => {
  const navigate = useNavigate();
  const { mutate: checkEmail } = useCheckEmail();
  const { toast } = useNotification();
  const {
    control,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm({
    defaultValues: { login: '' },
    resolver: zodResolver(loginValidationSchema),
  });

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    let value = e.target.value;
    const containsValidCharacters = /^[\d.-]+$/.test(value);

    if (containsValidCharacters) {
      value = cpfMask(value);
    }

    e.target.value = value;
  };

  const onSubmit: SubmitHandler<LoginValidationData> = async (data) => {
    checkEmail(data.login, {
      onSuccess: (response) => {
        if (response.isSso && response.ssoUrl) {
          window.location.href = response.ssoUrl;
          return;
        }

        nextStep(data.login);
      },
      onError: () => {
        setError('login', {
          type: 'server',
          message: 'E-mail ou CPF inválidos',
        });
      },
    });
  };

  const handleGoogleLogin = () => {
    const callbackUrl = window.location.origin;
    localStorage.setItem(LocalStorage.RedirectAfterLogin, callbackUrl);
    const googleOAuthUrl = `/account/auth/google_oauth2/login?callback_url=${callbackUrl}/auth_callback`;
    window.location.href = googleOAuthUrl;
  };

  const handleMicrosoftLogin = () => {
    const callbackUrl = window.location.origin;
    localStorage.setItem(LocalStorage.RedirectAfterLogin, callbackUrl);
    const microsoftOAuthUrl = `/account/auth/entra_id/login?callback_url=${callbackUrl}/auth_callback`;
    window.location.href = microsoftOAuthUrl;
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const authErrorParam = urlParams.get('authError');
    const errorMessage = urlParams.get('errorMessage');

    if (authErrorParam && errorMessage) {
      const message = decodeURIComponent(errorMessage);
      toast({ content: message, type: 'error' });
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, [toast]);

  return (
    <Stack gap={4}>
      <Typography fontSize={20}>
        Boas-vindas à sua plataforma de desenvolvimento.
      </Typography>
      <Stack gap={2}>
        <Button
          variant="outlined"
          fullWidth
          sx={{ justifyContent: 'flex-start', gap: 1 }}
          onClick={handleGoogleLogin}
        >
          <img src={googleLogo} alt="" width={23} height={23} />
          Entrar com Google
        </Button>
        <Button
          variant="outlined"
          fullWidth
          sx={{ justifyContent: 'flex-start', gap: 1 }}
          onClick={handleMicrosoftLogin}
        >
          <img src={microsoftLogo} alt="" width={23} height={23} />
          Entrar com Microsoft
        </Button>
      </Stack>
      <Box>
        <FormLabel id="login-label" htmlFor="login">
          Ou entre com seu e-mail ou CPF
        </FormLabel>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack direction="row" gap={2} my={1} alignItems="start">
            <Controller
              control={control}
              name="login"
              render={({ field }) => (
                <TextField
                  {...field}
                  autoFocus
                  id="login"
                  placeholder="E-mail ou CPF"
                  sx={{ flex: 1 }}
                  helperText={errors?.login?.message}
                  error={!!errors?.login?.message}
                  onChange={(e) => {
                    handleInputChange(e);
                    field.onChange(e.target.value);
                  }}
                />
              )}
            />
            <Button type="submit" variant="contained">
              <ArrowForward />
            </Button>
          </Stack>
        </form>
        <Link onClick={() => navigate('/login/esqueci-minha-senha')}>
          Esqueci minha senha
        </Link>
      </Box>
    </Stack>
  );
};
